/**
 * In-message status card for a tracked background task. Reads its task row from the
 * tasks React Query cache (by `jobId`) and renders working/completed/failed/stalled states.
 *
 * `useToolResultSweep` is what puts that row in the cache: it POSTs a long-task
 * row when it sees a submit result, and picks up backend-created MCP-task rows
 * on its next tasks-list refetch. So the card can render before its row exists,
 * which is what the `kind` prop and the rowless bail-out below are for.
 *
 * For long-task jobs the card also shows the status badge, result links, and
 * manual recovery actions via the pending-injection queue. Its detail and
 * timestamp rows render in every state, falling back to a placeholder and to the
 * submit time when the job has reported neither, so the card keeps one shape for
 * a job's whole life (ADR 059, "Fixed rows on the card").
 */
import React from "react";
import type {
  TaskDisplayState,
  TaskKind,
  TaskStatusValue,
  TerminalTaskStatus,
} from "types/TaskRegistry";
import {
  mcpTaskActionMessage,
  buildLongTaskActionMessage,
  deriveTaskCardPresentation,
  deriveTaskCardTimestamps,
  deriveTaskDisplayState,
  getTaskKind,
  shouldStopPolling,
  isKnownTerminalStatus,
  taskDetailPlaceholder,
} from "types/TaskRegistry";
import { isFetchTrackerInFlight } from "types/Task";
import { resumeTaskPolling } from "hooks/tasks/taskCacheWrites";
import {
  useEnqueueInjection,
  type PendingInjection,
} from "hooks/tasks/usePendingInjections";
import { getLongTaskGroup } from "hooks/tasks/useLongTaskGroups";
import { buildTerminalDirective } from "types/longTask";
import useTasksQuery from "services/dbApi/hooks/useTasksQuery";
import { useTaskCache } from "hooks/tasks/useTaskCache";
import { hasResultPayoff, isSafeHttpUrl } from "utilities/longTaskResult";
import styles from "./Message.module.scss";

function formatTimeOfDay(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString();
}

const STATUS_BADGE_TONE: Record<TaskStatusValue, string> = {
  pending: styles.taskStatusBadgeNeutral,
  working: styles.taskStatusBadgeInfo,
  completed: styles.taskStatusBadgeSuccess,
  failed: styles.taskStatusBadgeDanger,
  cancelled: styles.taskStatusBadgeCancelled,
};

const STATUS_BADGE_LABEL: Record<TaskStatusValue, string> = {
  pending: "Pending",
  working: "Working",
  completed: "Completed",
  failed: "Failed",
  cancelled: "Cancelled",
};

// `failed` and `cancelled` look identical and differ only in the label string
const CARD_TONE_CLASS: Record<TaskDisplayState, string> = {
  completed: styles.taskStatusCardCompleted,
  failed: styles.taskStatusCardFailed,
  cancelled: styles.taskStatusCardFailed,
  stalled: styles.taskStatusCardStalled,
  working: styles.taskStatusCardWorking,
};

const CARD_BUTTON_CLASS: Record<TaskDisplayState, string> = {
  completed: styles.taskStatusButtonSuccess,
  failed: styles.taskStatusButtonError,
  cancelled: styles.taskStatusButtonError,
  stalled: styles.taskStatusButtonWarning,
  working: styles.taskStatusButtonWarning,
};

interface TaskStatusProps {
  /**
   * The task's jobId (the row key this card reads its row under). Sourced
   * from the API `task_id`; for an MCP task the row's jobId is its API
   * `task_id`, so the two are equal.
   */
  jobId: string;
  toolName: string;
  /**
   * Which kind to assume while the row is still missing from the cache; once it
   * arrives, the row's own `kind` wins. `ToolCallDetails` passes "long-task" for a
   * submit tool that returned its `job_id` in the tool output itself, rather than
   * the API deferring the call and returning a task handle to poll. That card
   * renders as soon as the message does, before `useToolResultSweep` has POSTed
   * the row.
   */
  kind?: TaskKind;
  onAskAboutTask?: (prompt: string) => void;
  incognito?: boolean;
}

export default function TaskStatus({
  jobId,
  toolName,
  kind = "mcp-task",
  onAskAboutTask,
  incognito,
}: TaskStatusProps) {
  const enqueueInjection = useEnqueueInjection();
  const setTask = useTaskCache();

  const tasksQuery = useTasksQuery();
  const task = tasksQuery.data?.find((j) => j.jobId === jobId);

  const entryKind: TaskKind = task ? getTaskKind(task) : kind;
  const pollExhausted = task?.pollExhausted ?? false;
  const status: TaskStatusValue = task?.status ?? "pending";
  const isLongTask = entryKind === "long-task";
  const hasResults = hasResultPayoff(task?.resultLinks, task?.archiveUrl);

  // Memoized because this scans the user's whole cross-chat task list, and the
  // card re-renders on every streamed chunk of the message it sits in.
  const fetchInFlight = React.useMemo(
    () => isFetchTrackerInFlight(tasksQuery.data ?? [], jobId),
    [tasksQuery.data, jobId]
  );

  // Render nothing in incognito chats: the card has no persisted thread and no
  // polling, so it would only paint a dead pill with a disabled button. Placed
  // after all hooks to satisfy the rules of hooks.
  if (incognito) return null;

  // The submit tool call and its result are regular chat messages and stay in
  // the thread (not removed after dismissing). When you dismiss a finished job
  // in the tasks popup, it updates the record with `dismissed: true`, which will
  // then be excluded from `GET /chat/tasks`. Then, after the next reload, that
  // thread still shows a TaskStatus card for the submit message, but
  // tasksQuery.data.find(...) finds nothing.
  if (isLongTask && !task) return null;

  const enqueueLongTaskDirective = (
    content: string,
    injectionKind: PendingInjection["kind"]
  ) => {
    if (!task) return;
    // Every click delivers its own directive. The queue's duplicate suppression
    // keeps only one auto-generated directive per (jobId, kind), but these two
    // kinds opt out of that (see COMPOSITE_DEDUP_KINDS) and fall back to
    // matching on `id` (which a fresh UUID doesn't match on).
    // Clicking "Check Status" twice should ask twice.
    enqueueInjection(task.threadId, {
      id: crypto.randomUUID(),
      chatId: task.threadId,
      content,
      createdAt: new Date().toISOString(),
      longTaskJobId: task.jobId,
      kind: injectionKind,
    });
  };

  const handleCheckStatusLongTask = () => {
    if (!task) return;
    enqueueLongTaskDirective(
      buildLongTaskActionMessage(
        status,
        task.jobId,
        task.correlationId ?? undefined
      ),
      "long-task-check-status"
    );
  };

  const handleRetryFetchLongTask = () => {
    if (!task) return;
    const groupName = task.groupName ?? "";
    // Derived from the shared guard rather than an inline enumeration, so a new
    // terminal status is not silently relabelled "failed".
    const terminalKey: TerminalTaskStatus = isKnownTerminalStatus(status)
      ? status
      : "failed";
    enqueueLongTaskDirective(
      buildTerminalDirective(
        getLongTaskGroup(task.serverUrl ?? "", groupName),
        terminalKey,
        {
          groupName,
          jobId: task.jobId,
          correlationId: task.correlationId ?? undefined,
        },
        task.statusReason
      ),
      "long-task-retry-fetch"
    );
  };

  const handleCheckMcpTask = () => {
    if (onAskAboutTask) {
      onAskAboutTask(mcpTaskActionMessage(status, toolName, jobId));
    }
  };

  const handleRefresh = () => {
    resumeTaskPolling({ jobId, setTask });
  };

  // TODO: distinct pending UI. `pending` is now a reachable status but for
  // now folds into the "working" display via deriveTaskDisplayState.
  const displayState = deriveTaskDisplayState(status, pollExhausted);
  const presentation = deriveTaskCardPresentation(
    displayState,
    isLongTask,
    hasResults,
    fetchInFlight
  );
  const cardClass = CARD_TONE_CLASS[presentation.tone];
  const buttonClass = CARD_BUTTON_CLASS[presentation.tone];
  const { label, buttonLabel, suppressButton } = presentation;

  // Route the primary button by the presentation's action. Long-task cards fetch
  // or re-check via the injection queue; MCP-task cards ask through onAskAboutTask.
  const onButtonClick =
    presentation.action === "long-task-fetch"
      ? handleRetryFetchLongTask
      : presentation.action === "long-task-status"
      ? handleCheckStatusLongTask
      : handleCheckMcpTask;

  // An MCP-task card's button asks a question through onAskAboutTask; disable it
  // when no handler is wired (e.g. the popup-driven surface). Long-task buttons
  // drive the injection queue directly and are always enabled; the rowless case
  // that would make them no-ops returns null above.
  const buttonDisabled =
    presentation.action === "mcp-task-check" && !onAskAboutTask;

  const displayName = task?.displayName;
  const statusReason = task?.statusReason;
  // Only long-task cards show the "retrieving results" hint, and only while the
  // completed job's result-fetch tracker is still in flight.
  const showRetrievingHint = isLongTask && !hasResults && fetchInFlight;
  const badgeTooltip = statusReason ?? undefined;

  // A long-task card renders the detail and timestamp rows in every state, so it
  // does not change shape as fields arrive (ADR 059, "Fixed rows on the card").
  // An mcp-task card shows the detail row only when it has a real reason.
  let detailRow: React.ReactNode = null;
  if (statusReason) {
    detailRow = <div className={styles.taskStatusReason}>{statusReason}</div>;
  } else if (isLongTask) {
    detailRow = (
      <div className={styles.taskStatusPlaceholder}>
        {taskDetailPlaceholder(displayState)}
      </div>
    );
  }

  // First candidate whose stored value parses wins, so a corrupt timestamp falls
  // through to the submit time rather than dropping the row.
  let timestampText: string | null = null;
  if (isLongTask && task) {
    for (const candidate of deriveTaskCardTimestamps({
      displayState,
      createdAt: task.createdAt,
      statusCachedAt: task.statusCachedAt,
    })) {
      const time = formatTimeOfDay(candidate.iso);
      if (time) {
        timestampText = `${candidate.label} ${time}`;
        break;
      }
    }
  }

  return (
    <div className={`${styles.taskStatusCard} ${cardClass}`}>
      <div className={styles.taskStatusLabel}>
        <span className={styles.taskStatusDot}>●</span>
        <span>
          {label} <em>{isLongTask ? displayName ?? toolName : toolName}</em>
        </span>
        {isLongTask && (
          <span
            className={`${styles.taskStatusBadge} ${STATUS_BADGE_TONE[status]}`}
            title={badgeTooltip}
          >
            {STATUS_BADGE_LABEL[status]}
          </span>
        )}
      </div>
      {/* Second child, so DOM order matches the grid: this row sits in row 1
          next to the label, and a keyboard user reaches the buttons before the
          result links rendered below them. */}
      <div className={styles.taskStatusActions}>
        {pollExhausted && !shouldStopPolling(status) && (
          <button
            className={`${styles.taskStatusButton} ${styles.taskStatusButtonRefresh}`}
            onClick={handleRefresh}
          >
            Refresh
          </button>
        )}
        {!suppressButton && (
          <button
            className={`${styles.taskStatusButton} ${buttonClass}`}
            onClick={onButtonClick}
            disabled={buttonDisabled}
          >
            {buttonLabel}
          </button>
        )}
      </div>
      {detailRow}
      {showRetrievingHint && (
        <div className={styles.taskStatusReason}>Retrieving results…</div>
      )}
      {timestampText && (
        <div className={styles.taskStatusTimestamp}>{timestampText}</div>
      )}
      {isLongTask && hasResults && (
        <div className={styles.taskResultLinks}>
          {/* Re-validate the URL scheme at the render boundary, not just at the
              parse boundary: these values also arrive via the backend GET
              (mergeTasksPreservingClientState) and from rows persisted before
              the parse-side guard existed. React does not filter href schemes,
              so a stored javascript:/data: URL would execute on click. */}
          {isSafeHttpUrl(task?.archiveUrl) && (
            <a
              className={`${styles.taskResultLink} ${styles.taskResultLinkArchive}`}
              href={task.archiveUrl}
              target="_blank"
              rel="noreferrer"
            >
              Download archive
            </a>
          )}
          {task?.resultLinks
            ?.filter((report) => isSafeHttpUrl(report.permalink))
            .map((report) => (
              <a
                key={report.permalink}
                className={styles.taskResultLink}
                href={report.permalink}
                target="_blank"
                rel="noreferrer"
              >
                {report.label}
              </a>
            ))}
        </div>
      )}
    </div>
  );
}
